import cx from 'classnames';

// @ts-expect-error - no TypeScript definitions yet
import { AlertIcon } from 'src/assets/icons/Alert';
import { CustomButtonBase } from 'src/components/CustomButtonBase';
// @ts-expect-error - no TypeScript definitions yet
import { CustomChip } from 'src/components/CustomChip';
import { CustomCircularProgress } from 'src/components/CustomCircularProgress';
// @ts-expect-error - no TypeScript definitions yet
import { CustomDialog } from 'src/components/CustomDialog';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { type Channel, getChannelPerformanceLabel } from 'src/data/channel';
import type { Callback, VoidFunction } from 'src/lib/types';

import { META_BUSINESS_PORTFOLIO_HELP_URL, type MetaConnectChoice } from './lib';
import styles from './MetaConnectChoicePopup.module.scss';
import { useHandlers } from './useHandlers';

interface MetaConnectChoicePopupProps {
  className?: string;
  open?: boolean;
  channel: Channel;
  onResolve: Callback<MetaConnectChoice | undefined>;
  onClose: VoidFunction;
}

export const MetaConnectChoicePopup = ({
  className,
  open,
  channel,
  onResolve,
  onClose,
}: MetaConnectChoicePopupProps) => {
  const { bannerVisible, businessPending, onPickPersonal, onPickBusiness } = useHandlers({
    channel,
    onResolve,
    onClose,
  });

  if (!open) {
    return null;
  }

  const channelLabel = getChannelPerformanceLabel(channel);

  return (
    <CustomDialog
      className={cx(styles.root, className)}
      open={!businessPending}
      title={`Connect ${channelLabel}`}
      maxWidth="sm"
      data-testid="metaConnectChoicePopup"
      displayCloseIcon
      onClose={onClose}
      hideActions
    >
      <div className={styles.content}>
        {bannerVisible && (
          <div
            className={styles.banner}
            data-testid="metaConnectChoice_banner"
          >
            <AlertIcon className={styles.bannerIcon} />
            <div>
              <CustomTypography
                className={styles.bannerTitle}
                variant={TYPOGRAPHY_VARIANT.semiBold14}
              >
                Your business portfolio wasn&apos;t connected
              </CustomTypography>
              <CustomTypography
                className={styles.bannerText}
                variant={TYPOGRAPHY_VARIANT.regular14}
              >
                If your ad accounts aren&apos;t in a Meta business portfolio yet, you can{' '}
                <a
                  className={styles.bannerLink}
                  href={META_BUSINESS_PORTFOLIO_HELP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  learn how to connect a business portfolio
                </a>{' '}
                — or connect a personal account below.
              </CustomTypography>
            </div>
          </div>
        )}

        <CustomTypography
          className={styles.description}
          variant={TYPOGRAPHY_VARIANT.regular14}
        >
          {channelLabel} ad accounts hold valuable data about what drives engagement and conversion.
        </CustomTypography>
        <CustomTypography
          className={styles.subtitle}
          variant={TYPOGRAPHY_VARIANT.semiBold14}
        >
          How are your ad accounts managed?
        </CustomTypography>

        <div className={styles.options}>
          <CustomButtonBase
            className={cx(styles.option, styles.optionRecommended)}
            onClick={onPickBusiness}
            disabled={businessPending}
            data-testid="metaConnectChoice_business"
          >
            <div className={styles.optionInfo}>
              <div className={styles.optionHeader}>
                <CustomTypography
                  className={styles.optionTitle}
                  variant={TYPOGRAPHY_VARIANT.semiBold14}
                >
                  Business portfolio
                </CustomTypography>
                <CustomChip
                  label="Recommended"
                  size="small"
                />
              </div>
              <CustomTypography
                className={styles.optionDescription}
                variant={TYPOGRAPHY_VARIANT.regular14}
              >
                Choose which ad accounts Anyword can access. Requires a Meta business portfolio.
              </CustomTypography>
            </div>
            <span className={styles.optionAction}>
              {businessPending ? (
                <CustomCircularProgress
                  size={16}
                  color="inherit"
                />
              ) : (
                'Connect'
              )}
            </span>
          </CustomButtonBase>

          <CustomButtonBase
            className={styles.option}
            onClick={onPickPersonal}
            data-testid="metaConnectChoice_personal"
          >
            <div className={styles.optionInfo}>
              <CustomTypography
                className={styles.optionTitle}
                variant={TYPOGRAPHY_VARIANT.semiBold14}
              >
                Personal account
              </CustomTypography>
              <CustomTypography
                className={styles.optionDescription}
                variant={TYPOGRAPHY_VARIANT.regular14}
              >
                Anyword gets access to all your ad accounts, and connects your Facebook page and
                Instagram account too.
              </CustomTypography>
            </div>
            <span className={cx(styles.optionAction, styles.optionActionOutlined)}>Connect</span>
          </CustomButtonBase>
        </div>
      </div>
    </CustomDialog>
  );
};
